import { useEffect } from 'react';
import { useFormikContext } from 'formik';
import * as R from 'ramda';
import { errorHandler } from 'App/utils';
import PropTypes from 'prop-types';
import toast from 'Shared/toast';
import { useUserContext } from 'App/contexts/UserContext';
import useFetchEhrBranchSettings from 'App/hooks/useFetchEhrBranchSettings';

const propTypes = {
    paramFirstName: PropTypes.string,
    paramLastName: PropTypes.string,
    paramId: PropTypes.string,
    paramPhoneNumber: PropTypes.string,
};

const defaultProps = {
    paramFirstName: '',
    paramLastName: '',
    paramId: '',
    paramPhoneNumber: '',
};

// Maps the company's configured DISPLAYED_ID setting to the matching Find Patient search field.
// human_id (and any unrecognized/unset value) falls back to internalId.
const ID_SEARCH_FIELD_BY_DISPLAYED_ID = {
    other_id: 'otherId',
    external_id: 'externalId',
};

/**
 * When search params from the header are submitted, we need to make sure they are put into the form.
 */
function UpdateSearchParams({ paramFirstName, paramLastName, paramId, paramPhoneNumber }) {
    const { setFieldValue, setValues, validateForm, submitForm, initialValues } = useFormikContext();
    const initialSearchParams = initialValues.searchParams;
    const { company_permissions: companyPermissions } = useUserContext();
    const { data: ehrBranchSettings, isFetched: isEhrBranchSettingsFetched } = useFetchEhrBranchSettings();

    useEffect(() => {
        (async function validateAndSubmit() {
            // to handle patient search by name, ID or phone from the top nav
            // Start from a clean slate so a topbar search isn't merged with stale values left in the form.
            let newSearchParams;
            if (paramLastName) {
                newSearchParams = { ...initialSearchParams, firstName: paramFirstName || '', lastName: paramLastName };
            } else if (paramId) {
                const displayedId = companyPermissions?.DisplayedId;
                const ehrPrimaryIdType = ehrBranchSettings?.ehrPrimaryIdType;
                if (displayedId === 'ehr_id' && ehrPrimaryIdType) {
                    // Search by EHR ID requires pairing it with the branch's configured EHR type.
                    newSearchParams = {
                        ...initialSearchParams,
                        ehrId: paramId,
                        ehrPrimaryIdType: { keyValue: ehrPrimaryIdType },
                    };
                } else if (!isEhrBranchSettingsFetched) {
                    // Branch settings haven't loaded yet, so we don't yet know the true DISPLAYED_ID/EHR type combo.
                    return;
                } else if (displayedId === 'ehr_id') {
                    // No EHR type is configured for the branch, so fall back to the Dispensing ID.
                    newSearchParams = { ...initialSearchParams, externalId: paramId };
                } else {
                    const idFieldName = ID_SEARCH_FIELD_BY_DISPLAYED_ID[displayedId] || 'internalId';
                    newSearchParams = { ...initialSearchParams, [idFieldName]: paramId };
                }
            } else if (paramPhoneNumber) {
                newSearchParams = { ...initialSearchParams, phoneNumber: paramPhoneNumber };
            } else {
                return;
            }
            // shouldValidate is false since validateForm() below already runs a single validation pass.
            await setValues(R.assoc('searchParams', newSearchParams), false);
            const errors = await validateForm();
            if (Object.keys(errors).length > 0) {
                setFieldValue('data');
                toast.error(Object.values(errors)[0]);
            } else {
                submitForm().catch(errorHandler);
            }
        })();
    }, [
        paramFirstName,
        paramLastName,
        paramId,
        paramPhoneNumber,
        initialSearchParams,
        companyPermissions?.DisplayedId,
        ehrBranchSettings?.ehrPrimaryIdType,
        isEhrBranchSettingsFetched,
        setFieldValue,
        setValues,
        validateForm,
        submitForm,
    ]);

    return null;
}

UpdateSearchParams.propTypes = propTypes;
UpdateSearchParams.defaultProps = defaultProps;

export default UpdateSearchParams;
